/**
 * Habitat corridor detection and scoring — §5 + §7.2.
 *
 * Corridor rules (§5):
 * - A corridor for habitat H is a maximal connected set of tiles where every
 *   pair of adjacent tiles is habitat-connected on H via a directly-touching
 *   edge pair.
 * - Two tiles are habitat-connected on edge `e` iff tile A's edge `e` AND
 *   tile B's reciprocal edge both carry habitat H.
 * - A single tile that has H on at least one edge is itself a corridor of size 1.
 *
 * Scoring (§7.2):
 * - Each habitat scores only the largest corridor: score = size of that corridor.
 *
 * Landmark modifiers (plumbed via opts):
 * - minTwoTile: corridors of size 1 score 0 (H card effect).
 * - singleTileCounts: ignored when minTwoTile is also true (H card is stricter).
 *
 * Pure — no I/O, no mutations.
 */

import { TILE_DEFS_BY_ID } from "../data/tile-defs";
import type { AxialCoord } from "../types/coords";
import { coordKey, NEIGHBOUR_OFFSETS } from "../types/coords";
import type { Habitat } from "../types/habitat";
import type { Board } from "../types/player";
import type { TileRotation } from "../types/tile";
import { rotatedEdges } from "../utils/axial";

export interface CorridorOpts {
  /** H card: corridors of size 1 do not score. */
  readonly minTwoTile?: boolean;
  /** G card: single-tile corridors count (no-op when minTwoTile is true). */
  readonly singleTileCounts?: boolean;
}

export interface HabitatCorridor {
  readonly habitat: Habitat;
  readonly coords: readonly AxialCoord[];
}

// ---------------------------------------------------------------------------
// corridorScoreForSize
// ---------------------------------------------------------------------------

/**
 * Returns the score for a corridor of the given size per §7.2.
 * Score = size, with optional landmark overrides.
 *
 * Decision (spec ambiguity §7.2): The spec says "add its size to the score."
 * There is no separate lookup table — score equals size. Landmark H card
 * (minTwoTileCorridor) makes size-1 corridors score 0.
 */
export const corridorScoreForSize = (
  size: number,
  opts: CorridorOpts,
): number => {
  if (size <= 0) {
    return 0;
  }
  // H card: minimum two tiles — size 1 always scores 0 regardless of G card
  if (opts.minTwoTile === true && size === 1) {
    return 0;
  }
  return size;
};

// ---------------------------------------------------------------------------
// tileHasHabitatOnEdge — pure helper
// ---------------------------------------------------------------------------

/**
 * Returns true if the tile, **after applying its placed `rotation`**, carries
 * `habitat` on physical `edgeIndex`. Free rotation (2026-05-17) affects only
 * this end-game area-scoring path; `rotation === 0` is the identity, so
 * pre-rotation games are unchanged. Falls back to false when the tile def is
 * not found in the registry.
 */
const tileHasHabitatOnEdge = (
  tileDefId: string,
  rotation: TileRotation,
  edgeIndex: number,
  habitat: Habitat,
): boolean => {
  const def = TILE_DEFS_BY_ID[tileDefId];
  if (def === undefined) {
    return false;
  }
  return rotatedEdges(def.edges, rotation)[edgeIndex] === habitat;
};

// ---------------------------------------------------------------------------
// tileParticipatesInHabitat
// ---------------------------------------------------------------------------

/**
 * Returns true if the tile has `habitat` on at least one edge, making it a
 * candidate member of a corridor for that habitat.
 */
const tileParticipatesInHabitat = (
  tileDefId: string,
  habitat: Habitat,
): boolean => {
  const def = TILE_DEFS_BY_ID[tileDefId];
  if (def === undefined) {
    return false;
  }
  return def.edges.some((e) => e === habitat);
};

// ---------------------------------------------------------------------------
// findHabitatCorridors
// ---------------------------------------------------------------------------

/**
 * Finds all habitat corridors for `habitat` in the player's `board`.
 *
 * Two tiles are corridor-connected for `habitat` iff:
 * 1. They are edge-adjacent (one hex step).
 * 2. The shared edge on tile A carries `habitat`.
 * 3. The reciprocal edge on tile B also carries `habitat`.
 *
 * Returns all maximal connected components (corridors). Caller picks the
 * largest for scoring per §7.2.
 */
export const findHabitatCorridors = (
  board: Board,
  habitat: Habitat,
): readonly HabitatCorridor[] => {
  // Build the candidate set: all tiles that have `habitat` on at least one edge
  const candidates = new Map<string, AxialCoord>();
  for (const [key, cell] of Object.entries(board.cells)) {
    if (tileParticipatesInHabitat(cell.tileDefId, habitat)) {
      const [qStr, rStr] = key.split(",");
      if (qStr !== undefined && rStr !== undefined) {
        candidates.set(key, { q: Number(qStr), r: Number(rStr) });
      }
    }
  }

  if (candidates.size === 0) {
    return [];
  }

  const unvisited = new Set<string>(candidates.keys());
  const corridors: HabitatCorridor[] = [];

  for (const [startKey, startCoord] of candidates) {
    if (!unvisited.has(startKey)) {
      continue;
    }

    // BFS — only cross an edge if BOTH sides have `habitat` on that edge
    const regionCoords: AxialCoord[] = [];
    const queue: AxialCoord[] = [startCoord];
    unvisited.delete(startKey);

    while (queue.length > 0) {
      const current = queue.shift()!;
      regionCoords.push(current);

      const currentKey = coordKey(current);
      const currentCell = board.cells[currentKey];
      if (currentCell === undefined) {
        continue;
      }

      for (const offset of NEIGHBOUR_OFFSETS) {
        const neighbourCoord: AxialCoord = {
          q: current.q + offset.dq,
          r: current.r + offset.dr,
        };
        const neighbourKey = coordKey(neighbourCoord);

        if (!unvisited.has(neighbourKey)) {
          continue;
        }

        const neighbourCell = board.cells[neighbourKey];
        if (neighbourCell === undefined) {
          continue;
        }

        // Edge e on current tile must carry `habitat` (after rotation)
        const currentEdgeIsHabitat = tileHasHabitatOnEdge(
          currentCell.tileDefId,
          currentCell.rotation,
          offset.edge,
          habitat,
        );
        // Reciprocal edge on neighbour tile must also carry `habitat`
        const neighbourEdgeIsHabitat = tileHasHabitatOnEdge(
          neighbourCell.tileDefId,
          neighbourCell.rotation,
          offset.reciprocal,
          habitat,
        );

        if (currentEdgeIsHabitat && neighbourEdgeIsHabitat) {
          unvisited.delete(neighbourKey);
          queue.push(neighbourCoord);
        }
      }
    }

    corridors.push({ habitat, coords: regionCoords });
  }

  return corridors;
};
